/**
 * Role-aware notification link resolver.
 * Mirrors backend `_auto_link` in /app/backend/utils/notifications.py.
 *
 * Used as a client-side override when a stored notification link doesn't match
 * the current user's role — common with legacy notifications created before
 * the backend became role-aware.
 *
 * @param {string} title - Notification title
 * @param {string} role - Recipient role (admin/mro/cro/merchant/member)
 * @param {string} storedLink - The link saved on the notification (may be stale)
 * @returns {string} The corrected link, or storedLink if no override applies.
 */
const ROLE_RULES = {
  admin: [
    [['new member registered'], '/admin/users'],
    [['merchant application', 'merchant approved', 'merchant rejected',
      'new merchant', 'merchant partner'], '/admin/merchants'],
    [['withdrawal'], '/admin/withdrawals'],
    [['support ticket', 'new ticket', 'ticket reply'], '/admin/support'],
    [['new booking', 'service booked'], '/admin/merchants'],
    [['referral', 'commission', 'referred'], '/admin/users'],
  ],
  mro: [
    [['merchant application', 'merchant approved', 'merchant rejected',
      'new merchant', 'merchant partner'], '/mro/merchants'],
    [['commission', 'bonus'], '/mro/commissions'],
    [['mro role'], '/mro'],
  ],
  cro: [
    [['new member registered'], '/cro'],
    [['referral', 'commission', 'referred'], '/cro'],
  ],
  merchant: [
    [['merchant application', 'merchant approved', 'merchant rejected'], '/merchant'],
    [['new booking', 'service booked', 'booked'], '/merchant'],
    [['support ticket', 'ticket reply'], '/support'],
    [['wallet adjusted', 'wallet', 'transfer', 'withdrawal'], '/wallet'],
    [['subscription', 'plan', 'payment'], '/pricing'],
    [['id verif', 'identity', 'password', 'account'], '/profile'],
  ],
  member: [
    [['wallet adjusted', 'wallet', 'transfer'], '/wallet'],
    [['withdrawal'], '/wallet'],
    [['subscription', 'plan', 'payment'], '/pricing'],
    [['referral', 'commission', 'referred'], '/referrals'],
    [['support ticket', 'ticket reply'], '/support'],
    [['new booking', 'service booked', 'booked'], '/wallet'],
    [['id verif', 'identity'], '/profile'],
    [['password', 'account suspended', 'account restored'], '/profile'],
    [['service'], '/merchant-partners'],
  ],
};

const DEFAULT_RULES = [
  [['welcome'], '/dashboard'],
];

// Paths that obviously don't apply to a given role.
const ROLE_PATH_BLOCKLIST = {
  admin: ['/merchant', '/mro', '/cro', '/dashboard', '/wallet', '/referrals', '/pricing'],
  mro: ['/merchant', '/admin', '/cro'],
  cro: ['/merchant', '/admin', '/mro'],
  merchant: ['/admin', '/mro', '/cro', '/dashboard'],
  member: ['/admin', '/mro', '/cro', '/merchant'],
};

function resolveByTitle(title, role) {
  const t = (title || '').toLowerCase();
  const rules = ROLE_RULES[role] || ROLE_RULES.member;
  for (const tier of [rules, DEFAULT_RULES]) {
    for (const [keywords, path] of tier) {
      for (const kw of keywords) {
        if (t.includes(kw)) return path;
      }
    }
  }
  return '';
}

/**
 * Re-resolve the link only if the stored one doesn't fit the user's role.
 * Returns either the corrected path or the original storedLink.
 */
export function resolveNotificationLink(title, role, storedLink) {
  if (!storedLink) return resolveByTitle(title, role) || '';
  const blocked = ROLE_PATH_BLOCKLIST[role] || [];
  // Stored link starts with a blocked top-level path for this role → override.
  const isBlocked = blocked.some((seg) => storedLink === seg || storedLink.startsWith(seg + '/') || storedLink.startsWith(seg + '?'));
  if (isBlocked) {
    const corrected = resolveByTitle(title, role);
    if (corrected) return corrected;
  }
  return storedLink;
}
